import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface PersonelKayit {
  id: string;
  ad: string;
  soyad: string;
  telefon: string;
  email: string | null;
  gorev: string;
  aktif: boolean;
  ise_baslangic_tarihi: string | null;
  notlar: string;
  bu_ay_servis?: number;
  toplam_servis?: number;
}

export const GOREV_SECENEKLERI = [
  'Teknik Servis',
  'Montaj Personeli',
  'Ofis Personeli',
  'Yönetici',
  'Diğer',
] as const;

export function usePersoneller() {
  const [personeller, setPersoneller] = useState<PersonelKayit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('sadem_personeller')
        .select('*')
        .order('ad');

      if (err) throw err;

      const now = new Date();
      const ayBaslangic = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];

      const mapped: PersonelKayit[] = await Promise.all((data || []).map(async (p: any) => {
        const { count: buAy } = await supabase
          .from('sadem_servis_kayitlari')
          .select('*', { count: 'exact', head: true })
          .eq('personel_id', p.id)
          .eq('aktif', true)
          .gte('servis_tarihi', ayBaslangic);

        const { count: toplam } = await supabase
          .from('sadem_servis_kayitlari')
          .select('*', { count: 'exact', head: true })
          .eq('personel_id', p.id)
          .eq('aktif', true);

        return {
          id: p.id,
          ad: p.ad,
          soyad: p.soyad,
          telefon: p.telefon,
          email: p.email || null,
          gorev: p.gorev,
          aktif: p.aktif,
          ise_baslangic_tarihi: p.ise_baslangic_tarihi,
          notlar: p.notlar || '',
          bu_ay_servis: buAy || 0,
          toplam_servis: toplam || 0,
        };
      }));

      setPersoneller(mapped);
    } catch (err: any) {
      setError(err.message || 'Personel verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { personeller, loading, error, refetch: fetchData };
}

export async function createPersonel(data: {
  ad: string;
  soyad: string;
  telefon: string;
  email?: string;
  gorev: string;
  diger_gorev?: string;
  notlar?: string;
}) {
  const payload: any = {
    ad: data.ad,
    soyad: data.soyad,
    telefon: data.telefon,
    gorev: data.gorev === 'Diğer' && data.diger_gorev ? data.diger_gorev : data.gorev,
    aktif: true,
    ise_baslangic_tarihi: new Date().toISOString().split('T')[0],
    notlar: data.notlar || '',
  };
  if (data.email) payload.email = data.email;

  const { error } = await supabase.from('sadem_personeller').insert(payload);
  if (error) {
    console.error('[createPersonel] hata:', { tablo: 'sadem_personeller', code: error.code, message: error.message, details: error.details, hint: error.hint });
    throw error;
  }
}

export async function updatePersonel(id: string, data: Partial<PersonelKayit> & { diger_gorev?: string }) {
  const payload: any = {
    ad: data.ad,
    soyad: data.soyad,
    telefon: data.telefon,
    gorev: data.gorev === 'Diğer' && data.diger_gorev ? data.diger_gorev : data.gorev,
    aktif: data.aktif,
    notlar: data.notlar,
  };
  if (data.email !== undefined) payload.email = data.email;

  const { error } = await supabase.from('sadem_personeller').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deletePersonel(id: string) {
  const { error } = await supabase.from('sadem_personeller').update({ aktif: false }).eq('id', id);
  if (error) throw error;
}